import { useEffect, useState } from 'react'
import { formatCents } from '@core/money'
import {
  entryLabel,
  loadLedger,
  undoLedgerEntry,
  type LedgerEntry,
  type Payable,
} from '@core/payments'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * "Ik heb per ongeluk op betaald getikt."
 *
 * Every other action on a payment adds something. This is the only one that
 * takes something back, so it stays closed until you ask for it, and it lists
 * what is actually there rather than guessing which entry you meant — an undo
 * that silently picks the wrong row is worse than no undo.
 *
 * Nothing is deleted. The entry stays in the ledger, marked as taken back,
 * and the history keeps saying what happened.
 */
export function UndoEntry({
  payable,
  onDone,
}: {
  payable: Payable
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    loadLedger(payable.instalmentId)
      .then(setEntries)
      .catch(() => setEntries([]))
  }, [open, payable.instalmentId])

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full py-2 text-sm text-muted">
        Toch niet betaald
      </button>
    )
  }

  return (
    <Card className="text-left">
      <p className="font-medium mb-1">Wat wil je terugnemen?</p>
      <p className="text-sm text-muted mb-3">
        Het blijft in de geschiedenis staan, maar telt niet meer mee.
      </p>

      <ErrorNote>{error}</ErrorNote>

      {entries === null && <p className="text-sm text-muted">Even geduld…</p>}

      {entries?.length === 0 && (
        <p className="text-sm text-muted">Er is hier niets geboekt.</p>
      )}

      <div className="space-y-2">
        {(entries ?? []).map((entry) => (
          <div key={entry.id} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm">
                {entryLabel(entry.kind)}
                {entry.amountCents > 0 && ` · ${formatCents(entry.amountCents)}`}
              </p>
              <p className="text-xs text-muted">{formatDate(entry.occurredOn)}</p>
            </div>
            <button
              disabled={busy === entry.id}
              onClick={async () => {
                setBusy(entry.id)
                setError('')
                try {
                  await undoLedgerEntry(entry.id)
                  onDone()
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Terugnemen lukte niet.')
                  setBusy(null)
                }
              }}
              className="shrink-0 text-sm underline underline-offset-2 py-1"
            >
              {busy === entry.id ? '…' : 'Terugnemen'}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Laat maar
        </Button>
      </div>
    </Card>
  )
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T00:00:00`))
}
